/**
 * EVENTSFLO-CLIENT-PORTAL-PHASE-2-PROJECT-SHIFT-REQUESTS-V1
 * Client Project Requests — list + submit form + detail modal.
 *
 * Both admin and viewer client_users can submit and view requests.
 * Requests are read-only after submission (status managed by tenant admin).
 */
import { useState, useEffect, useCallback } from "react";
import { useOutletContext, Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Separator } from "../../components/ui/separator";
import { ScrollArea } from "../../components/ui/scroll-area";
import { toast } from "sonner";
import {
  Plus,
  Loader2,
  Filter,
  Inbox,
  Calendar,
  Users,
  MapPin,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  ArrowRight,
  Pencil,
  Trash2,
} from "lucide-react";
import { useI18n } from "../../i18n/I18nProvider";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const STATUS_LABELS = {
  submitted: "Submitted",
  under_review: "Under review",
  accepted: "Accepted",
  rejected: "Rejected",
  converted: "Converted to project",
  // EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-EDIT-DELETE-V1
  cancelled_by_client: "Cancelled by you",
};

const EDITABLE_STATUSES = new Set(["submitted", "under_review"]);

const statusBadge = (status) => {
  switch (status) {
    case "submitted":
      return "bg-blue-500/10 text-blue-600 border-blue-500/20";
    case "under_review":
      return "bg-amber-500/10 text-amber-600 border-amber-500/20";
    case "accepted":
      return "bg-emerald-500/10 text-emerald-600 border-emerald-500/20";
    case "converted":
      return "bg-purple-500/10 text-purple-600 border-purple-500/20";
    case "rejected":
      return "bg-red-500/10 text-red-600 border-red-500/20";
    case "cancelled_by_client":
      return "bg-zinc-500/10 text-zinc-600 border-zinc-500/20";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const statusIcon = (status) => {
  switch (status) {
    case "submitted":
      return <Inbox className="h-3 w-3" />;
    case "under_review":
      return <Clock className="h-3 w-3" />;
    case "accepted":
      return <CheckCircle2 className="h-3 w-3" />;
    case "converted":
      return <ArrowRight className="h-3 w-3" />;
    case "rejected":
      return <XCircle className="h-3 w-3" />;
    default:
      return null;
  }
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
};

export default function ClientProjectRequestsPage() {
  const { getToken } = useOutletContext();
  const navigate = useNavigate();
  const { t } = useI18n();

  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, total_pages: 0 });
  const [statusFilter, setStatusFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  // EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-EDIT-DELETE-V1
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);

  const fetchRequests = useCallback(
    async (status = "all") => {
      const token = getToken();
      if (!token) return;
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: "1", page_size: "50" });
        if (status && status !== "all") params.append("status", status);
        const r = await fetch(
          `${API_URL}/api/client/project-requests?${params}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!r.ok) throw new Error("Failed to fetch requests");
        const data = await r.json();
        setRequests(data.data || []);
        setMeta(data.meta || { total: 0, page: 1, total_pages: 0 });
      } catch (err) {
        console.error("Requests fetch error:", err);
        toast.error(t("clientPortal:projectRequests.failedToLoad"));
      } finally {
        setLoading(false);
      }
    },
    [getToken]
  );

  useEffect(() => {
    fetchRequests(statusFilter);
  }, [fetchRequests, statusFilter]);

  const openDetail = (req) => {
    setDetail(req);
    setDetailOpen(true);
  };

  // EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-EDIT-DELETE-V1
  const canEditCancel = detail && EDITABLE_STATUSES.has(detail.status);

  const handleEdit = () => {
    if (!detail) return;
    setDetailOpen(false);
    navigate(`/client/project-requests/${detail.id}/edit`);
  };

  const openCancel = () => {
    setCancelConfirm(false);
    setCancelOpen(true);
  };

  const handleCancel = async () => {
    if (!detail) return;
    const token = getToken();
    if (!token) return;
    setCancelling(true);
    try {
      const r = await fetch(
        `${API_URL}/api/client/project-requests/${detail.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || `Cancel failed (${r.status})`);
      }
      toast.success("Request cancelled. Our team has been notified.");
      setCancelOpen(false);
      setDetailOpen(false);
      fetchRequests(statusFilter);
    } catch (err) {
      console.error("Cancel error:", err);
      toast.error(err.message || "Failed to cancel request");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold" data-testid="requests-title">
            Project Requests
          </h1>
          <p className="text-sm text-muted-foreground">
            {meta.total} request{meta.total !== 1 ? "s" : ""} —{" "}
            <span className="italic">requests, not confirmed bookings</span>
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger
              className="w-[180px]"
              data-testid="request-status-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>
                  {v}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => navigate("/client/project-requests/new")}
            data-testid="new-request-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Request
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent
            className="py-12 text-center"
            data-testid="requests-empty-state"
          >
            <Inbox className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">
              No project requests yet
            </p>
            <Button asChild>
              <Link to="/client/project-requests/new">
                <Plus className="h-4 w-4 mr-2" />
                Submit your first request
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {requests.map((req) => (
            <Card
              key={req.id}
              className="hover:bg-accent/30 transition-colors cursor-pointer"
              onClick={() => openDetail(req)}
              data-testid={`request-row-${req.id}`}
            >
              <CardContent className="py-4">
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 bg-primary/10 text-primary rounded-lg flex items-center justify-center">
                    <Inbox className="h-5 w-5" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="font-semibold text-sm">
                        {req.project_name}
                      </h3>
                      <Badge
                        variant="outline"
                        className={`text-[10px] capitalize gap-1 ${statusBadge(req.status)}`}
                      >
                        {statusIcon(req.status)}
                        {STATUS_LABELS[req.status] || req.status}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {req.start_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(req.start_date)}
                          {req.end_date && req.end_date !== req.start_date
                            ? ` → ${formatDate(req.end_date)}`
                            : ""}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {req.shifts?.length || 0} shift
                        {(req.shifts?.length || 0) !== 1 ? "s" : ""}
                      </span>
                      {req.venue && (
                        <span className="flex items-center gap-1 truncate max-w-[200px]">
                          <MapPin className="h-3 w-3" />
                          {req.venue}
                        </span>
                      )}
                    </div>
                    {req.status === "rejected" && req.rejection_reason && (
                      <div className="mt-2 text-xs text-red-600 bg-red-500/10 p-2 rounded">
                        <strong>{t("clientPortal:projectRequests.rejectedPrefix")}</strong> {req.rejection_reason}
                      </div>
                    )}
                  </div>

                  <div className="text-xs text-muted-foreground shrink-0">
                    Submitted {formatDate(req.created_at)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent
          className="max-w-2xl max-h-[85vh]"
          data-testid="request-detail-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap">
              <Inbox className="h-5 w-5 text-primary" />
              {detail?.project_name}
              {detail?.status && (
                <Badge
                  variant="outline"
                  className={`text-[10px] capitalize gap-1 ${statusBadge(detail.status)}`}
                >
                  {statusIcon(detail.status)}
                  {STATUS_LABELS[detail.status] || detail.status}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {detail && (
            <ScrollArea className="max-h-[60vh] pr-4">
              <div className="space-y-4 text-sm">
                {detail.status === "rejected" && detail.rejection_reason && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md">
                    <p className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Rejected
                    </p>
                    <p className="text-sm">{detail.rejection_reason}</p>
                  </div>
                )}

                {detail.status === "converted" && detail.converted_project_id && (
                  <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-md">
                    <p className="text-xs font-semibold text-purple-700 mb-1">
                      Converted to project
                    </p>
                    <Link
                      to={`/client/projects/${detail.converted_project_id}`}
                      className="text-sm text-purple-700 hover:underline inline-flex items-center gap-1"
                      data-testid="client-converted-project-link"
                    >
                      Open project
                      <ArrowRight className="h-3 w-3" />
                    </Link>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Event Type</p>
                    <p className="font-medium">
                      {detail.event_type || "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Submitted</p>
                    <p className="font-medium">
                      {formatDate(detail.created_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("clientPortal:projectRequests.startDate")}</p>
                    <p className="font-medium">
                      {detail.start_date ? formatDate(detail.start_date) : "TBC"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">{t("clientPortal:projectRequests.endDate")}</p>
                    <p className="font-medium">
                      {detail.end_date ? formatDate(detail.end_date) : "TBC"}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground">Venue</p>
                    <p className="font-medium">{detail.venue || "—"}</p>
                    {detail.venue_address && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {detail.venue_address}
                      </p>
                    )}
                  </div>
                  {detail.meeting_point && (
                    <div className="col-span-2">
                      <p className="text-xs text-muted-foreground">
                        Meeting Point
                      </p>
                      <p className="font-medium">{detail.meeting_point}</p>
                    </div>
                  )}
                </div>

                <Separator />

                <div>
                  <h4 className="text-sm font-semibold mb-2">
                    Shifts ({detail.shifts?.length || 0})
                  </h4>
                  <div className="space-y-2">
                    {(detail.shifts || []).map((s, i) => (
                      <div
                        key={i}
                        className="p-3 border border-border/60 rounded-md text-xs"
                        data-testid={`request-detail-shift-${i}`}
                      >
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-medium">
                          <span>{formatDate(s.date)}</span>
                          <span>
                            {s.start_time} – {s.end_time}
                          </span>
                          <Badge variant="secondary" className="text-[10px]">
                            {s.staff_required}× staff
                          </Badge>
                          {s.role && (
                            <span className="text-muted-foreground">
                              {s.role}
                            </span>
                          )}
                        </div>
                        {(s.activity || s.location) && (
                          <p className="text-muted-foreground mt-1">
                            {s.activity}
                            {s.location ? ` • ${s.location}` : ""}
                          </p>
                        )}
                        {s.notes && (
                          <p className="text-muted-foreground mt-1 italic">
                            {s.notes}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {detail.on_site_contact && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        On-site Contact
                      </p>
                      <p className="font-medium">
                        {detail.on_site_contact.name || "—"}
                      </p>
                      <div className="text-xs text-muted-foreground">
                        {detail.on_site_contact.phone && (
                          <span>{detail.on_site_contact.phone}</span>
                        )}
                        {detail.on_site_contact.email && (
                          <span className="ml-2">
                            {detail.on_site_contact.email}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {detail.notes && (
                  <>
                    <Separator />
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        Additional Notes
                      </p>
                      <p className="text-sm whitespace-pre-wrap">
                        {detail.notes}
                      </p>
                    </div>
                  </>
                )}
              </div>
            </ScrollArea>
          )}

          {/* EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-EDIT-DELETE-V1 */}
          <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
            <Button
              variant="outline"
              onClick={() => setDetailOpen(false)}
              data-testid="client-request-close-btn"
            >
              Close
            </Button>
            {canEditCancel && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={openCancel}
                  data-testid="client-request-cancel-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Cancel request
                </Button>
                <Button
                  onClick={handleEdit}
                  data-testid="client-request-edit-btn"
                >
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit request
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EVENTSFLO-CLIENT-PORTAL-PROJECT-REQUEST-EDIT-DELETE-V1 */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent
          className="sm:max-w-md"
          data-testid="client-request-cancel-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Cancel this request?
            </DialogTitle>
            <DialogDescription>
              This will cancel the request and notify the iCrew team. It
              cannot be undone from the portal.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm space-y-3">
            <p>
              You are about to cancel{" "}
              <span className="font-medium">
                {detail?.project_name}
              </span>
              .
            </p>
            <label className="flex items-start gap-2 text-xs">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={cancelConfirm}
                onChange={(e) => setCancelConfirm(e.target.checked)}
                data-testid="client-request-cancel-confirm-checkbox"
              />
              <span>
                I understand this cancels the request. Our team will see it
                marked as cancelled by me.
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCancelOpen(false)}
              disabled={cancelling}
              data-testid="client-request-cancel-back-btn"
            >
              Back
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancel}
              disabled={!cancelConfirm || cancelling}
              data-testid="client-request-cancel-submit-btn"
            >
              {cancelling && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              Cancel request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
